import { createFileRoute } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { Loading } from "@/Loading";
import dayjs from "dayjs";
import {
  useUserUsageDetail,
  type UserUsageDetail,
} from "~/services/customer/usageHistory";
import { useEffect } from "react";
import { useLoadingStore } from "../../../stores/loading";

export const Route = createFileRoute(
  "/customer/layout/user-usage-history-detail"
)({
  component: UserUsageHistoryDetail,
  validateSearch: (search: Record<string, unknown>) => ({
    reservationSeq: Number(search.reservationSeq) || 0,
  }),
});

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  return dayjs(value).format("YYYY.MM.DD HH:mm");
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return dayjs(value).format("YYYY.MM.DD");
}

function formatTime(value: string | null | undefined): string {
  if (!value) return "-";
  // 백엔드에서 "HH:mm:ss" 형식으로 내려옴
  return value.slice(0, 5);
}

function buildReservPeriodText(detail: UserUsageDetail): string {
  if (!detail.dtReservStart) return "-";
  const start = formatDate(detail.dtReservStart);
  const end = detail.dtReservEnd ? formatDate(detail.dtReservEnd) : "";
  const hasStartT = !!detail.startTime;
  const hasEndT = !!detail.endTime;
  const startT = hasStartT ? formatTime(detail.startTime) : "";
  const endT = hasEndT ? formatTime(detail.endTime) : "";

  switch (detail.cdReservType) {
    case "RESERV_TYPE_HOTEL":
      // 호텔: 입실일 + 시간 ~ 퇴실일 + 시간
      if (!hasStartT && !hasEndT) {
        return end && end !== start ? `${start} ~ ${end}` : start;
      }
      return `${start}${startT ? ` ${startT}` : ""} ~ ${end || start}${
        endT ? ` ${endT}` : ""
      }`;
    case "RESERV_TYPE_GROOMING":
      // 미용: 단일 날짜 + 시작 시간 (시간 없으면 일자만)
      return hasStartT ? `${start} ${startT}` : start;
    case "RESERV_TYPE_CLASS":
    case "RESERV_TYPE_DAYCARE":
    default:
      // 수업/데이케어: 단일 일자 + 시간 범위 (시간 없으면 일자만)
      if (!hasStartT && !hasEndT) return start;
      return `${start} ${startT || "-"} ~ ${endT || "-"}`;
  }
}

function UserUsageHistoryDetail() {
  const { reservationSeq } = Route.useSearch();
  const { data, isLoading, isFetching, error } = useUserUsageDetail(
    reservationSeq,
    { enabled: reservationSeq > 0 }
  );

  const { toggleLoading } = useLoadingStore();
  useEffect(() => {
    toggleLoading(isLoading || isFetching);
  }, [isLoading, isFetching, toggleLoading]);

  if (!reservationSeq) {
    return (
      <>
        <LayoutHeader title="이용 내역 상세" hasBackBtn hasFootPrint={false} />
        <div className="py-20 text-sm text-center text-gray-500">
          잘못된 접근입니다.
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <LayoutHeader title="이용 내역 상세" hasBackBtn hasFootPrint={false} />
        <div className="flex justify-center items-center flex-auto">
          <Loading visible={true} />
        </div>
      </>
    );
  }

  const detail = data?.data;
  if (error || !detail) {
    return (
      <>
        <LayoutHeader title="이용 내역 상세" hasBackBtn hasFootPrint={false} />
        <div className="py-20 text-sm text-center text-red-500">
          상세 정보를 불러오지 못했습니다.
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader title="이용 내역 상세" hasBackBtn hasFootPrint={false} />
      <div className="px-3 pt-5 pb-24 flex-auto bg-[#F5F5F5]">
        {/* 반려견 정보 */}
        <div className="p-4 mb-3 bg-white rounded-xl border border-[#E9E9E9]">
          <div className="flex gap-3 items-center">
            <div className="overflow-hidden w-14 h-14 bg-gray-100 rounded-full shrink-0">
              {detail.petThumbnailUrl ? (
                <img
                  src={detail.petThumbnailUrl}
                  alt={detail.petName}
                  className="object-cover w-full h-full"
                />
              ) : (
                <div className="flex justify-center items-center w-full h-full text-xs text-gray-400">
                  N
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-gray-900 truncate">
                {detail.petName}
              </p>
              {detail.companyName && (
                <p className="text-sm text-gray-600 truncate">
                  {detail.companyName}
                </p>
              )}
              {detail.reservation && (
                <p className="mt-0.5 text-xs text-orange-500">
                  {detail.reservation}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 예약 정보 */}
        <div className="mb-3">
          <p className="px-1 mb-1.5 text-sm font-medium text-gray-700">
            예약 정보
          </p>
          <div className="bg-white rounded-xl border border-[#E9E9E9] divide-y divide-gray-100">
            <Row label="이용 일자" value={buildReservPeriodText(detail)} />
            {detail.everyWeek && <Row label="반복" value="매주 반복" />}
            {detail.ticketName && (
              <Row label="사용 이용권" value={detail.ticketName} />
            )}
            {detail.memo && <Row label="메모" value={detail.memo} multiline />}
          </div>
        </div>

        {/* 등하원 정보 */}
        <div className="mb-3">
          <p className="px-1 mb-1.5 text-sm font-medium text-gray-700">
            등·하원 정보
          </p>
          <div className="bg-white rounded-xl border border-[#E9E9E9] divide-y divide-gray-100">
            <Row label="등원 시간" value={formatDateTime(detail.checkInTime)} />
            <Row
              label="하원 시간"
              value={formatDateTime(detail.checkOutTime)}
            />
            <Row label="총 이용 시간" value={detail.durationText ?? "-"} />
          </div>
        </div>

      </div>
    </>
  );
}

interface RowProps {
  label: string;
  value: string;
  multiline?: boolean;
}

const Row = ({ label, value, multiline = false }: RowProps) => (
  <div className="flex justify-between gap-3 items-start px-4 py-3">
    <span className="text-xs text-gray-500 shrink-0 pt-0.5">{label}</span>
    <span
      className={`text-sm text-gray-800 text-right ${
        multiline ? "whitespace-pre-wrap" : "truncate"
      }`}
    >
      {value}
    </span>
  </div>
);
